import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Me } from '@vuokramani/shared';
import { buttonClass, Spinner } from '../../components/ui.tsx';
import { api } from '../../lib/api.ts';
import { homePath, meQueryKey } from '../../lib/auth.ts';
import s from './auth.module.css';

/**
 * Sähköpostilinkki osoittaa tänne: /kirjaudu/vahvista#<tunniste>.
 * Tunniste lähetetään POST-kutsulla, jottei linkkiskanneri kuluta sitä.
 */
export function VerifyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const started = useRef(false);
  const [noToken, setNoToken] = useState(false);

  const verify = useMutation({
    mutationFn: (token: string) => api<Me>('/auth/verify', { method: 'POST', body: { token } }),
    onSuccess: (me) => {
      queryClient.setQueryData(meQueryKey, me);
      void navigate(homePath(me), { replace: true });
    },
  });

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = window.location.hash.slice(1);
    // Tunniste pois osoiteriviltä ja selainhistoriasta.
    window.history.replaceState(null, '', window.location.pathname);
    if (token) verify.mutate(token);
    else setNoToken(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const failed = verify.isError || noToken;

  return (
    <div className={s.center}>
      <div className={s.centerCard}>
        {failed ? (
          <>
            <h1 className={s.title}>{t('verify.failedTitle')}</h1>
            <p className={s.lead}>{t('verify.failedText')}</p>
            <Link to="/kirjaudu" className={buttonClass('primary')}>
              {t('verify.requestNew')}
            </Link>
          </>
        ) : (
          <>
            <Spinner />
            <p className={s.lead}>{t('verify.checking')}</p>
          </>
        )}
      </div>
    </div>
  );
}
